import React, { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { AlertTriangle, BadgeCheck, Film, ImageIcon, Loader2, Play, Star } from 'lucide-react';
import mediaService from '@services/mediaService';
import type { MediaAsset, MediaStatus } from '@/types/media';
import { MEDIA_STATUS_LABELS } from '@/types/media';
import { formatDuration } from '@/utils/formatters';

/** Badge de status do processamento (só aparece fora de `ready`, a não ser com `always`). */
export const MediaStatusBadge: React.FC<{ status: MediaStatus; always?: boolean; className?: string }> = ({
  status,
  always = false,
  className,
}) => {
  if (status === 'ready' && !always) return null;
  const cls =
    status === 'ready'
      ? 'badge-success'
      : status === 'error'
        ? 'badge-danger'
        : status === 'processing'
          ? 'badge-warning'
          : 'badge-neutral';
  return (
    <span className={clsx(cls, 'gap-1', className)}>
      {status === 'processing' && <Loader2 size={10} className="animate-spin" />}
      {status === 'error' && <AlertTriangle size={10} />}
      {MEDIA_STATUS_LABELS[status]}
    </span>
  );
};

interface MediaThumbProps {
  media: MediaAsset;
  className?: string;
  /** Selos (vídeo, status, preferencial, liberada) sobre a miniatura. */
  showBadges?: boolean;
  /** `cover` (grade) ou `contain` (comparação lado a lado). */
  fit?: 'cover' | 'contain';
}

/**
 * Miniatura de uma mídia. A `thumb_url` é uma SAS de ~15 min: se a imagem falhar (SAS vencida),
 * pede uma nova em GET /media/:id/url?variant=thumb — uma vez só, para não entrar em laço.
 */
export const MediaThumb: React.FC<MediaThumbProps> = ({ media, className, showBadges = true, fit = 'cover' }) => {
  const [src, setSrc] = useState<string | undefined>(media.thumb_url);
  const [retried, setRetried] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setSrc(media.thumb_url);
    setRetried(false);
    setFailed(false);
  }, [media.id, media.thumb_url]);

  const handleError = async () => {
    if (retried) {
      setFailed(true);
      return;
    }
    setRetried(true);
    try {
      const fresh = await mediaService.getUrl(media.id, 'thumb');
      setSrc(fresh.url);
    } catch {
      setFailed(true);
    }
  };

  const Placeholder = media.kind === 'video' ? Film : ImageIcon;

  return (
    <div className={clsx('relative overflow-hidden bg-slate-100', className)}>
      {src && !failed ? (
        <img
          src={src}
          alt={media.title || media.original_file_name}
          loading="lazy"
          decoding="async"
          onError={handleError}
          className={clsx('w-full h-full', fit === 'cover' ? 'object-cover' : 'object-contain')}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-slate-300">
          <Placeholder size={32} />
        </div>
      )}

      {showBadges && (
        <>
          <div className="absolute top-1.5 left-1.5 flex flex-col items-start gap-1">
            <MediaStatusBadge status={media.status} />
          </div>
          <div className="absolute top-1.5 right-1.5 flex items-center gap-1">
            {media.is_preferred && (
              <span className="rounded-full bg-white/90 p-1 shadow" title="Preferencial">
                <Star size={12} className="text-amber-500 fill-amber-400" />
              </span>
            )}
            {media.is_released_for_customers && (
              <span className="rounded-full bg-white/90 p-1 shadow" title="Liberada para clientes">
                <BadgeCheck size={12} className="text-emerald-600" />
              </span>
            )}
          </div>
          {media.kind === 'video' && (
            <span className="absolute bottom-1.5 right-1.5 inline-flex items-center gap-1 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-medium text-white">
              <Play size={10} className="fill-white" />
              {media.duration_seconds !== undefined ? formatDuration(media.duration_seconds) : 'Vídeo'}
            </span>
          )}
        </>
      )}
    </div>
  );
};

export default MediaThumb;
